import { BLUE, GREEN, INK, PRIMARY } from '../../Theme/palette';
import { PrintHeader, PrintSheet } from '../../PrintSheet';
import { PrinterOutlined } from '@ant-design/icons';
import { SharedEmpty } from '@components';
import { Service, DispachedServicePart } from '@services';
import { Utils } from '@utils';
import { Button, Checkbox, Modal, Space, Switch, Typography } from 'antd';
import React, { useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';

const { Text } = Typography;

interface IProps {
  open: boolean;
  onClose: () => void;
  services: Service[];
  partsMap: Map<number, DispachedServicePart[]>;
  tenantVat: number;
  orderTitle?: string;
  registrationNumber?: string;
  customerName?: string;
  mileage?: number;
}

const cellHeader: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  color: '#555',
  textTransform: 'uppercase',
  letterSpacing: 0.3,
  padding: '6px 8px',
  borderBottom: '1px solid #888',
  textAlign: 'left',
};

const cellBody: React.CSSProperties = {
  fontSize: 11,
  padding: '6px 8px',
  borderBottom: '1px solid #ddd',
  verticalAlign: 'top',
};

const sectionTitle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  margin: '16px 0 8px',
  color: INK,
};

export const OrderServicePrintModal: React.FC<IProps> = ({
  open,
  onClose,
  services,
  partsMap,
  tenantVat,
  orderTitle,
  registrationNumber,
  customerName,
  mileage,
}) => {
  const [selectedIds, setSelectedIds] = useState<number[]>(services.map((s) => s.id));
  const [includeCosts, setIncludeCosts] = useState(true);

  // Only on opening. Keyed on the services as well, any re-render of the job while the modal is
  // up would put the ticks the user had just cleared straight back.
  React.useEffect(() => {
    if (!open) return;
    setSelectedIds(services.map((s) => s.id));
    setIncludeCosts(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const contentRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ contentRef, documentTitle: orderTitle || 'Job details' });

  const selectedServices = useMemo(
    () => services.filter((s) => selectedIds.includes(s.id)),
    [services, selectedIds]
  );

  const toggle = (id: number, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id)
    );
  };

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? services.map((s) => s.id) : []);
  };

  // The same sum the printed sheet shows per service, so the picker can price each row.
  const serviceTotal = (s: Service) => {
    const parts = partsMap.get(s.id) || [];
    const sub = Utils.calculateLabourPrice(s);
    const sVat = s.isVat ? Utils.roundToTwoDecimals((sub * tenantVat) / 100) : 0;
    const pPrice = Utils.calculatePartsPrice(parts);
    const pVat = parts.reduce(
      (a, p) => a + Utils.calculateVat({ ...p, duration: p.quantity, vat: tenantVat }),
      0
    );
    return Utils.roundToTwoDecimals(sub + sVat + pPrice + pVat);
  };

  const grandTotal = useMemo(
    () => selectedServices.reduce((acc, s) => acc + serviceTotal(s), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedServices, partsMap, tenantVat]
  );

  const allChecked = services.length > 0 && selectedIds.length === services.length;
  const noneChecked = selectedIds.length === 0;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <PrinterOutlined style={{ color: PRIMARY }} />
          <span>Print job details</span>
        </span>
      }
      width={640}
      footer={
        // What is about to come out of the printer, opposite the button that does it.
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <Text type="secondary" style={{ fontSize: 13 }}>
            {noneChecked
              ? 'Nothing selected'
              : `${selectedServices.length} of ${services.length} ${
                  services.length === 1 ? 'service' : 'services'
                }${includeCosts ? ` · ${Utils.formatCurrency(grandTotal)}` : ''}`}
          </Text>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              disabled={noneChecked}
              onClick={() => handlePrint()}
            >
              Print
            </Button>
          </Space>
        </div>
      }
    >
      {services.length === 0 ? (
        <SharedEmpty description="No work on this job to print" />
      ) : (
        <>
          {/* The whole row is the target, not the 14px checkbox - and each one carries what it
              will add to the sheet, so picking is done on the numbers rather than on the name. */}
          <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                padding: '10px 12px',
                background: '#fafafa',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <Checkbox
                checked={allChecked}
                indeterminate={!allChecked && !noneChecked}
                onChange={(e) => toggleAll(e.target.checked)}
              >
                <Text strong>Select all</Text>
              </Checkbox>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {selectedServices.length}/{services.length}
              </Text>
            </div>
            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
              {services.map((s) => {
                const checked = selectedIds.includes(s.id);
                const parts = partsMap.get(s.id) || [];
                return (
                  <div
                    key={s.id}
                    onClick={() => toggle(s.id, !checked)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      cursor: 'pointer',
                      background: checked ? BLUE.soft : '#fff',
                      borderBottom: '1px solid #f5f5f5',
                      transition: 'background 0.15s',
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => toggle(s.id, e.target.checked)}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                        {Utils.formatServiceLabel(s)}
                      </div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {s.isFixed ? 'Fixed price' : `${s.duration}h labour`}
                        {parts.length > 0 &&
                          ` · ${parts.length} ${parts.length === 1 ? 'part' : 'parts'}`}
                      </Text>
                    </div>
                    {includeCosts && (
                      <Text strong style={{ flexShrink: 0 }}>
                        {Utils.formatCurrency(serviceTotal(s))}
                      </Text>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Prices off, the sheet becomes a worksheet for the floor rather than a quote. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginTop: 12,
              padding: 12,
              background: '#f7fafd',
              border: '1px solid #e8eef5',
              borderRadius: 8,
            }}
          >
            <div>
              <div style={{ fontWeight: 600 }}>Include costs</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Off, the sheet shows the work and the parts without any prices
              </Text>
            </div>
            <Switch checked={includeCosts} onChange={setIncludeCosts} />
          </div>
        </>
      )}

      <div style={{ position: 'fixed', left: -10000, top: 0 }}>
        <PrintSheet ref={contentRef}>
          <PrintHeader
            title="Job details"
            subtitle={orderTitle}
            customerName={customerName}
            registrationNumber={registrationNumber}
            mileage={mileage}
          />

          {selectedServices.map((s, idx) => {
            const parts = partsMap.get(s.id) || [];
            const subTotal = Utils.calculateLabourPrice(s);
            const vatAmount = s.isVat ? Utils.roundToTwoDecimals((subTotal * tenantVat) / 100) : 0;
            const partsPrice = Utils.calculatePartsPrice(parts);
            const partsVat = parts.reduce(
              (a, p) => a + Utils.calculateVat({ ...p, duration: p.quantity, vat: tenantVat }),
              0
            );
            const total = Utils.roundToTwoDecimals(subTotal + vatAmount + partsPrice + partsVat);

            return (
              <div
                key={s.id}
                style={{
                  pageBreakInside: 'avoid',
                  marginBottom: 16,
                  paddingTop: idx === 0 ? 0 : 12,
                  borderTop: idx === 0 ? 'none' : '1px dashed #888',
                }}
              >
                <div style={sectionTitle}>
                  {Utils.formatServiceLabel(s)}
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '2px 6px',
                      borderRadius: 4,
                      background: s.isFixed ? BLUE.soft : GREEN.soft,
                      color: s.isFixed ? BLUE.tag : GREEN.tag,
                    }}
                  >
                    {s.isFixed ? 'Fixed' : 'Labour'}
                  </span>
                </div>

                {includeCosts ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 6 }}>
                    <thead>
                      <tr>
                        <th style={cellHeader}>{s.isFixed ? 'Price (£)' : '£/hour'}</th>
                        <th style={cellHeader}>Duration</th>
                        <th style={cellHeader}>Disc. %</th>
                        <th style={{ ...cellHeader, textAlign: 'right' }}>Sub total</th>
                        <th style={{ ...cellHeader, textAlign: 'right' }}>VAT</th>
                        <th style={{ ...cellHeader, textAlign: 'right' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={cellBody}>{Utils.formatCurrency(s.price)}</td>
                        <td style={cellBody}>{s.duration}</td>
                        <td style={cellBody}>{s.discount}</td>
                        <td style={{ ...cellBody, textAlign: 'right' }}>
                          {Utils.formatCurrency(subTotal)}
                        </td>
                        <td style={{ ...cellBody, textAlign: 'right' }}>
                          {Utils.formatCurrency(vatAmount)}
                        </td>
                        <td style={{ ...cellBody, textAlign: 'right', fontWeight: 700 }}>
                          {Utils.formatCurrency(Utils.roundToTwoDecimals(subTotal + vatAmount))}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 6 }}>
                    <thead>
                      <tr>
                        <th style={cellHeader}>Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={cellBody}>{s.duration}</td>
                      </tr>
                    </tbody>
                  </table>
                )}

                {s.note && (
                  <div style={{ fontSize: 11, marginBottom: 6 }}>
                    <strong>Note:</strong> {s.note}
                  </div>
                )}

                {parts.length > 0 && (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 700, margin: '6px 0 4px' }}>
                      Parts & extras
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={cellHeader}>Name</th>
                          <th style={cellHeader}>Reference</th>
                          <th style={cellHeader}>Type</th>
                          {includeCosts && (
                            <>
                              <th style={{ ...cellHeader, textAlign: 'right' }}>Cost</th>
                              <th style={{ ...cellHeader, textAlign: 'right' }}>Markup %</th>
                              <th style={{ ...cellHeader, textAlign: 'right' }}>Sale</th>
                            </>
                          )}
                          <th style={{ ...cellHeader, textAlign: 'right' }}>Qty</th>
                          {includeCosts && (
                            <>
                              <th style={{ ...cellHeader, textAlign: 'right' }}>Disc. %</th>
                              <th style={{ ...cellHeader, textAlign: 'right' }}>Sub</th>
                              <th style={{ ...cellHeader, textAlign: 'right' }}>VAT</th>
                              <th style={{ ...cellHeader, textAlign: 'right' }}>Total</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {parts.map((p, i) => {
                          const partSub = Utils.calculateLabourPrice({
                            isFixed: true,
                            price: (p.price || 0) * (p.quantity || 0),
                            discount: p.discount || 0,
                            duration: 1,
                          });
                          const partVat = Utils.calculateVat({
                            ...p,
                            duration: p.quantity,
                            vat: tenantVat,
                          });
                          const partTotal = Utils.roundToTwoDecimals(partSub + partVat);
                          return (
                            <tr key={`${s.id}-p-${i}`}>
                              <td style={cellBody}>{p.name}</td>
                              <td style={cellBody}>{p.number || ''}</td>
                              <td style={cellBody}>{p.type || 'Part'}</td>
                              {includeCosts && (
                                <>
                                  <td style={{ ...cellBody, textAlign: 'right' }}>
                                    {Utils.formatCurrency(p.costPrice || 0)}
                                  </td>
                                  <td style={{ ...cellBody, textAlign: 'right' }}>
                                    {p.margin ?? 0}
                                  </td>
                                  <td style={{ ...cellBody, textAlign: 'right' }}>
                                    {Utils.formatCurrency(p.price || 0)}
                                  </td>
                                </>
                              )}
                              <td style={{ ...cellBody, textAlign: 'right' }}>{p.quantity ?? 0}</td>
                              {includeCosts && (
                                <>
                                  <td style={{ ...cellBody, textAlign: 'right' }}>
                                    {p.discount ?? 0}
                                  </td>
                                  <td style={{ ...cellBody, textAlign: 'right' }}>
                                    {Utils.formatCurrency(partSub)}
                                  </td>
                                  <td style={{ ...cellBody, textAlign: 'right' }}>
                                    {Utils.formatCurrency(partVat)}
                                  </td>
                                  <td style={{ ...cellBody, textAlign: 'right', fontWeight: 700 }}>
                                    {Utils.formatCurrency(partTotal)}
                                  </td>
                                </>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </>
                )}

                {includeCosts && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      marginTop: 8,
                      fontSize: 13,
                    }}
                  >
                    <strong style={{ marginRight: 12 }}>Service total:</strong>
                    <span style={{ fontWeight: 700 }}>{Utils.formatCurrency(total)}</span>
                  </div>
                )}
              </div>
            );
          })}

          {includeCosts && selectedServices.length > 1 && (
            <div
              style={{
                borderTop: `2px solid ${INK}`,
                paddingTop: 8,
                marginTop: 12,
                display: 'flex',
                justifyContent: 'flex-end',
                fontSize: 14,
              }}
            >
              <strong style={{ marginRight: 12 }}>Grand total:</strong>
              <span style={{ fontWeight: 700 }}>
                {Utils.formatCurrency(Utils.roundToTwoDecimals(grandTotal))}
              </span>
            </div>
          )}
        </PrintSheet>
      </div>
    </Modal>
  );
};
